import React from "react";
import { localized, useLanguage } from '../i18n/translations';
import { Link } from "react-router-dom";
import { useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom';
import apiUrl from "../components/Url";
import CustomImage from "../components/CustomImage";
import PriceDisplay from "../components/PriceDisplay";

import ThankYouForOrdering from "../components/ThankYouForOrdering";
import Footer from "../components/Footer";
import { pixelInitiateCheckout, pixelPurchase } from "../components/MetaPixel";
import { getCustomOptionPriceModifier, formatCustomOptionDisplay } from "../components/customOptionUtils";
import { PET_ACCESSORIES, getCustomDataExtrasTotalPrice, getPetCount, getExtraPetsPrice, getAccessoryDescription, getAccessoryLabel } from "../components/petAccessoryUtils";
import { getCartLeadDays, formatLeadDates } from "../components/deliveryEstimate";
import { getOrderAttribution } from "../utils/track";
import { CARD_PAYMENT, CARD_PAYMENTS_ENABLED, isCardPaymentAvailable } from "../components/cardPayment";
import InlineCardPayment from "../components/InlineCardPayment";
import visaLogo from "../images/visa.svg";
import mastercardLogo from "../images/mastercard.svg";
import { phoneRuleFor, phoneError, localPhone } from "../components/phoneRules";
import { REGIONAL_COUNTRIES, sortedEuCountries, isEuOnlyCountry } from "../components/countries";
import { currencyForCountry } from "../utils/countryPricing";

// A country the select offers. Visitors from anywhere else must pick one before ordering.
const isShippableCountry = (code) =>
  REGIONAL_COUNTRIES.some(c => c.code === code) || (CARD_PAYMENTS_ENABLED && isEuOnlyCountry(code));

const CASH_ON_DELIVERY = "Placaćanje preuzećem";

// Floating-label inputs — defined outside Checkout so typing doesn't remount them
const fieldClass = (hasError) =>
  `peer w-full rounded-lg border bg-white px-3.5 pt-6 pb-2 text-[15px] text-gray-900 outline-none transition placeholder-transparent focus:border-brownSkin focus:ring-2 focus:ring-brownSkin/20 ${hasError ? 'border-red-400' : 'border-gray-300'}`;

function Field({ label, name, value, onChange, error, type = "text", autoComplete, inputMode, className = "" }) {
  return (
    <div className={className}>
      <div className="relative">
        <input
          id={`checkout-${name}`}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          inputMode={inputMode}
          placeholder={label}
          aria-invalid={!!error}
          className={fieldClass(!!error)}
        />
        <label
          htmlFor={`checkout-${name}`}
          className="pointer-events-none absolute left-3.5 top-2 text-xs text-gray-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-[15px] peer-focus:top-2 peer-focus:text-xs"
        >
          {label}
        </label>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// Phone with the shipping country's prefix fixed in front — the number must be from that country
function PhoneField({ label, country, value, onChange, error }) {
  const rule = phoneRuleFor(country);
  return (
    <div>
      <div className={`flex rounded-lg border bg-white transition focus-within:border-brownSkin focus-within:ring-2 focus-within:ring-brownSkin/20 ${error ? 'border-red-400' : 'border-gray-300'}`}>
        <span className="flex items-center gap-1.5 pl-3.5 pr-3 border-r border-gray-200 text-[15px] text-gray-700 select-none" aria-hidden="true">
          <span className="text-lg leading-none">{rule.flag}</span>+{rule.dial}
        </span>
        <div className="relative flex-1 min-w-0">
          <input
            id="checkout-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={value}
            onChange={onChange}
            placeholder={rule.example}
            aria-invalid={!!error}
            aria-label={`${label} (+${rule.dial})`}
            className="peer w-full rounded-r-lg bg-transparent px-3.5 pt-6 pb-2 text-[15px] text-gray-900 outline-none placeholder-transparent focus:placeholder-gray-400"
          />
          <label
            htmlFor="checkout-phone"
            className="pointer-events-none absolute left-3.5 top-2 text-xs text-gray-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-[15px] peer-focus:top-2 peer-focus:text-xs"
          >
            {label}
          </label>
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function SelectField({ label, name, value, onChange, error, children }) {
  return (
    <div>
      <div className="relative">
        <select
          id={`checkout-${name}`}
          name={name}
          value={value}
          onChange={onChange}
          className={`${fieldClass(!!error)} appearance-none pr-10 cursor-pointer`}
        >
          {children}
        </select>
        <label htmlFor={`checkout-${name}`} className="pointer-events-none absolute left-3.5 top-2 text-xs text-gray-500">
          {label}
        </label>
        <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function SummaryItem({ product, quantity, customOption, customData, setData, currency, language, translate }) {
  const extras = getCustomDataExtrasTotalPrice(customData, currency);
  const isOnSale = !setData && product.is_on_sale && product.sale_discount_percentage > 0;
  const optionModifier = setData ? 0 : getCustomOptionPriceModifier(product.custom_options, customOption, currency);
  const unitPrice = setData?.price?.[currency] !== undefined
    ? parseFloat(setData.price[currency]) + extras
    : parseFloat(isOnSale ? product[`discounted_${currency}`] : product[currency]) + optionModifier + extras;
  const unitOriginal = parseFloat(product[currency]) + optionModifier + extras;

  const hasDetails = customData && (customData.optionInputText || customData.description || customData.color || customData.date || customData.secondPet ||
    customData.images?.length > 0 || customData.accessories?.length > 0 || customData.customAccessories?.length > 0);

  return (
    <div className="flex gap-4 py-3">
      <div className="relative flex-shrink-0">
        <div className="w-16 h-16 rounded-lg overflow-hidden border border-darkerSkin bg-white">
          <CustomImage src={product.imageUrl} alt={product.imageAlt} className="w-16 h-16 object-cover" />
        </div>
        <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-gray-700 text-white text-xs font-medium flex items-center justify-center">
          {quantity}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 leading-snug">{localized(product, 'name', language)}</p>
        {setData?.label && <p className="text-xs text-gray-500 mt-0.5">{translate("Set")}: {setData.label}</p>}
        {!setData && customOption && (
          <p className="text-xs text-gray-500 mt-0.5">{formatCustomOptionDisplay(product?.custom_options, customOption, language)}</p>
        )}

        {hasDetails && (
          <div className="mt-1.5 text-xs text-gray-500 space-y-1">
            {customData.optionInputText && <div>{translate("Text")}: <span className="text-gray-700">{customData.optionInputText}</span></div>}
            {customData.color && <div>{translate("Color")}: <span className="text-gray-700">{customData.color}</span></div>}
            {customData.secondPet && (
              <div>{translate("Number of pets")}: <span className="text-gray-700">{getPetCount(customData)} (+{getExtraPetsPrice(customData, currency).toFixed(2)} {currency})</span></div>
            )}
            {customData.accessories?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <span>{translate("Accessories")}:</span>
                {customData.accessories.map(id => {
                  const acc = PET_ACCESSORIES.find(a => a.id === id);
                  if (!acc) return null;
                  return acc.image ? (
                    <img key={id} src={acc.image} alt={getAccessoryLabel(acc, language)} title={getAccessoryDescription(acc, customData?.priceProfile, language)} className="w-7 h-7 object-contain rounded border border-darkerSkin bg-white p-0.5" />
                  ) : (
                    <span key={id} title={getAccessoryDescription(acc, customData?.priceProfile, language)} className="w-7 h-7 flex items-center justify-center text-base rounded border border-darkerSkin bg-white">{acc.emoji}</span>
                  );
                })}
              </div>
            )}
            {customData.customAccessories?.length > 0 && (
              <div>{translate("Custom accessories")}: <span className="text-gray-700">{customData.customAccessories.join(", ")}</span></div>
            )}
            {customData.description && (
              <div className="break-words">{translate("Description")}: <span className="text-gray-700">{customData.description}</span></div>
            )}
            {customData.date && <div>{translate("Date")}: <span className="text-gray-700">{customData.date}</span></div>}
            {customData.images?.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-0.5">
                {customData.images.map((image, idx) => (
                  <img key={idx} src={image} alt={translate("Reference images")} className="h-9 w-9 object-cover rounded border border-darkerSkin" />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="text-right flex-shrink-0">
        <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{(unitPrice * quantity).toFixed(2)} {currency}</p>
        {isOnSale && (
          <p className="text-xs text-gray-400 line-through whitespace-nowrap">{(unitOriginal * quantity).toFixed(2)} {currency}</p>
        )}
      </div>
    </div>
  );
}

export default function Checkout({ cartItems, country, setCountry, shippingInfo, shippingPrice: passedShippingPrice, productsData, totalSubtotal, currency, RemoveLocalStorage, translate, categories, apiCountry, shippingAvailable = true}) {

  const { language } = useLanguage();
  const [showThankYou, setShowThankYou] = React.useState(false)
  const [apiData, setApiData] = React.useState([]);
  const [shippingPrice, setShippingPrice] = React.useState(passedShippingPrice || 0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [summaryOpen, setSummaryOpen] = React.useState(false);


  const location = useLocation();
  const { OrderNowProduct, currentQuantity, customOption, category, fromCart, categories: locationCategories } = location.state || {};


  const navigate = useNavigate();

  React.useEffect(() => {
    if ((OrderNowProduct && Object.keys(OrderNowProduct).length > 0) || (cartItems && cartItems.length > 0)) {
    } else {
      if (showThankYou === false) {
        navigate('/');
      }
    }
  }, [OrderNowProduct, cartItems, navigate]);

  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      "currency": currency,
      "country": isShippableCountry(country) ? country : ""
    }));
  }, [currency, country])


  React.useEffect(() => {

    window.scrollTo(0, 0);
  }, [showThankYou]);


  React.useEffect(() => {
    setShippingPrice(passedShippingPrice || 0);
  }, [passedShippingPrice]);






  const AvailableCartItems = React.useMemo(() => {
    return cartItems
      .map(item => {
        const product = productsData.find(data => data.id === item.id);
        if (!product || product.available !== 1 || !product.is_in_stock) return null;

        if (item.setData) {
          const isSetAvailable = (item.setData.items || []).every(setItem => {
            const p = productsData.find(pd => pd.id === setItem.product_id);
            return p && p.available && p.is_in_stock;
          });
          if (!isSetAvailable) return null;
        }

        return item;
      })
      .filter(item => item !== null);
  }, [cartItems, productsData]);

  // Compute selectedCustomOptions and OrderProducts
  const selectedCustomOptions = React.useMemo(() => {
    const options = [];
    if (OrderNowProduct) {
      if (customOption) {
        options.push({ productId: OrderNowProduct.id, customOption: customOption });
      }
    } else {
      AvailableCartItems.forEach(cartItem => {
        if (cartItem.customOption) {
          options.push({ productId: cartItem.id, customOption: cartItem.customOption });
        }
      });
    }
    return options;
  }, [OrderNowProduct, customOption, AvailableCartItems]);

  // Collect custom data from cart items (for products like "personalni plaanj")
  const customProductData = React.useMemo(() => {
    const customData = [];
    if (OrderNowProduct) {
      // Handle OrderNowProduct case
      const orderNowCustomData = location.state?.customData || null;
      if (orderNowCustomData) {
        customData.push({
          productId: OrderNowProduct.id,
          description: orderNowCustomData.description,
          date: orderNowCustomData.date,
          color: orderNowCustomData.color || null,
          accessories: orderNowCustomData.accessories || [],
          customAccessories: orderNowCustomData.customAccessories || [],
          secondPet: orderNowCustomData.secondPet || false,
          petCount: orderNowCustomData.petCount || null,
          priceProfile: orderNowCustomData.priceProfile || 'default',
          accessoriesModifier: getCustomDataExtrasTotalPrice(orderNowCustomData, currency),
          // Images will be handled separately in FormData (upload) and added on backend
        });
      }
    } else {
      AvailableCartItems.forEach(cartItem => {
        if (cartItem.customData) {
          customData.push({
            productId: cartItem.id,
            cartKey: cartItem.cartKey,
            description: cartItem.customData.description,
            date: cartItem.customData.date,
            color: cartItem.customData.color || null,
            accessories: cartItem.customData.accessories || [],
            customAccessories: cartItem.customData.customAccessories || [],
            secondPet: cartItem.customData.secondPet || false,
            petCount: cartItem.customData.petCount || null,
            priceProfile: cartItem.customData.priceProfile || 'default',
            accessoriesModifier: getCustomDataExtrasTotalPrice(cartItem.customData, currency),
            optionInputText: cartItem.customData.optionInputText || null,
            // Images will be handled separately in FormData (upload) and added on backend
          });
        }
      });
    }
    return customData;
  }, [OrderNowProduct, AvailableCartItems, location.state, currency]);

  // Collect selected sets from cart items
  const selectedSets = React.useMemo(() => {
    const sets = [];
    if (!OrderNowProduct) {
      AvailableCartItems.forEach(cartItem => {
        if (cartItem.setData) {
          sets.push({ cartKey: cartItem.cartKey, setLabel: cartItem.setData.label });
        }
      });
    }
    return sets;
  }, [OrderNowProduct, AvailableCartItems]);

  const OrderProducts = React.useMemo(() => {
    return OrderNowProduct ? [{ id: OrderNowProduct.id, qty: currentQuantity }] : AvailableCartItems;
  }, [OrderNowProduct, currentQuantity, AvailableCartItems]);

  // Enrich order products with item_price in the user's current currency
  const enrichedOrderProducts = React.useMemo(() => {
    return OrderProducts.map(item => {
      const product = productsData.find(p => p.id === item.id);
      if (!product) return { ...item, item_price: 0 };

      let price = product.is_on_sale && product.sale_discount_percentage > 0
        ? product[`discounted_${currency}`]
        : product[currency];

      const cartItem = AvailableCartItems.find(ci => ci.id === item.id && ci.qty === item.qty);

      if (cartItem?.setData?.price?.[currency] !== undefined) {
        price = cartItem.setData.price[currency];
      } else if (cartItem?.customOption) {
        const modifier = getCustomOptionPriceModifier(product.custom_options, cartItem.customOption, currency);
        if (modifier) price = parseFloat(price) + modifier;
      }

      return { ...item, item_price: parseFloat(price) || 0 };
    });
  }, [OrderProducts, productsData, currency, AvailableCartItems]);

  // Fire InitiateCheckout once when the page loads with items
  const hasTrackedCheckout = React.useRef(false);
  React.useEffect(() => {
    if (!hasTrackedCheckout.current && enrichedOrderProducts.length > 0) {
      hasTrackedCheckout.current = true;
      pixelInitiateCheckout(enrichedOrderProducts, totalSubtotal, currency);
    }
  }, [enrichedOrderProducts, totalSubtotal, currency]);

  let ItemsInCart;

  if (OrderNowProduct) {
    // For OrderNowProduct, check if there's customData in location.state
    const orderNowCustomData = location.state?.customData || null;
    ItemsInCart = <SummaryItem product={OrderNowProduct} quantity={currentQuantity} customOption={customOption} customData={orderNowCustomData} currency={currency} language={language} translate={translate} />;
  } else {
    // If OrderNowProduct doesn't exist, map over productsData
    ItemsInCart = productsData.map((product) => {
      const cartItem = AvailableCartItems.map((cartItem) => {
        if (cartItem.id === product.id) {
          return <SummaryItem key={cartItem.cartKey || cartItem.id} product={product} quantity={cartItem.qty} customOption={cartItem.customOption} customData={cartItem.customData} setData={cartItem.setData} currency={currency} language={language} translate={translate} />;
        }
        return null;
      });

      // Filter out the undefined values
      const filteredCartItem = cartItem.filter((item) => item !== null);

      return filteredCartItem;
    });
  }

  if (OrderNowProduct) {
    totalSubtotal = OrderProducts.map((item) => {
      const matchedProduct = productsData.find((product) => product.id === item.id);

      if (matchedProduct) {
        // Use discounted price if product is on sale, otherwise use regular price
        let basePrice = matchedProduct.is_on_sale && matchedProduct.sale_discount_percentage > 0 
          ? matchedProduct[`discounted_${currency}`] 
          : matchedProduct[currency];
        
        // Add custom option price modifier if applicable (supports multi-group format)
        const orderNowModifier = getCustomOptionPriceModifier(matchedProduct.custom_options, customOption, currency);
        if (orderNowModifier) basePrice = parseFloat(basePrice) + orderNowModifier;

        const orderNowAccessoriesModifier = getCustomDataExtrasTotalPrice(location.state?.customData, currency);
        if (orderNowAccessoriesModifier) basePrice = parseFloat(basePrice) + orderNowAccessoriesModifier;

        const subtotal = basePrice * item.qty;
        // Round to avoid floating point precision issues
        const roundedSubtotal = parseFloat(subtotal.toFixed(2));
        return roundedSubtotal.toString();
      }


    }).reduce((total, subtotal) => parseFloat(total) + parseFloat(subtotal), 0).toFixed(2);
  }

  const totalAmount = (Number(totalSubtotal) + Number(shippingPrice)).toFixed(2);






  const [formData, setFormData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: isShippableCountry(country) ? country : "",
    currency: currency,
    address: "",
    city: "",
    zip: "",
    paymentMethod: CASH_ON_DELIVERY,
    orderedItems: JSON.stringify(OrderProducts),
    customOptions: JSON.stringify(selectedCustomOptions),
    customProductData: JSON.stringify(customProductData),
    selectedSets: JSON.stringify(selectedSets)
  });

  // Update formData when OrderProducts, selectedCustomOptions, customProductData, or selectedSets change
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      orderedItems: JSON.stringify(OrderProducts),
      customOptions: JSON.stringify(selectedCustomOptions),
      customProductData: JSON.stringify(customProductData),
      selectedSets: JSON.stringify(selectedSets)
    }));
  }, [OrderProducts, selectedCustomOptions, customProductData, selectedSets]);

  // Free shipping logic is now handled in index.js and passed via passedShippingPrice prop



  const [formErrors, setFormErrors] = React.useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Card payment for Croatia / the EU (EUR) and Serbia (RSD, Monri); pouzeće only within the region.
  const cardPaymentAvailable = isCardPaymentAvailable(formData.country, formData.currency);
  const cashOnDeliveryAvailable = !isEuOnlyCountry(formData.country);
  React.useEffect(() => {
    if (!cardPaymentAvailable && formData.paymentMethod === CARD_PAYMENT) {
      setFormData(prev => ({ ...prev, paymentMethod: CASH_ON_DELIVERY }));
    } else if (!cashOnDeliveryAvailable && cardPaymentAvailable && formData.paymentMethod !== CARD_PAYMENT) {
      setFormData(prev => ({ ...prev, paymentMethod: CARD_PAYMENT }));
    }
  }, [cardPaymentAvailable, cashOnDeliveryAvailable, formData.paymentMethod]);

  // Stripe's key for card fields on this page. Without one, EUR cards pay on /placanje like Serbia.
  const cardFields = React.useRef(null);
  const [stripeKey, setStripeKey] = React.useState(null);
  React.useEffect(() => {
    fetch(`${apiUrl}/api/payments/config`, { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then(data => setStripeKey(data.publishableKey || null))
      .catch(() => {});
  }, []);

  // Stripe only: neither of these is saved on the order. The name follows the shipping name until edited.
  const [nameOnCard, setNameOnCard] = React.useState(null);
  const cardName = nameOnCard ?? `${formData.firstName} ${formData.lastName}`.trim();
  const [sameBillingAddress, setSameBillingAddress] = React.useState(true);
  const [billing, setBilling] = React.useState({ billingCountry: "", billingAddress: "", billingCity: "", billingZip: "" });

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBilling(prev => ({ ...prev, [name]: value }));
  };

  // Set once a card order exists but its payment failed: retries charge that same order, so
  // nothing that went into it can change. Stays on this page, the buyer only fixes the card.
  const [placedOrderId, setPlacedOrderId] = React.useState(null);
  const [cardError, setCardError] = React.useState(null);

  const chargeInline = async (inlinePayment, orderId) => {
    const { next, error } = await inlinePayment.pay(orderId, {
      name: cardName.trim(),
      email: formData.email,
      phone: formData.phone,
      address: sameBillingAddress
        ? { line1: formData.address, city: formData.city, postal_code: formData.zip, country: formData.country }
        : { line1: billing.billingAddress, city: billing.billingCity, postal_code: billing.billingZip, country: billing.billingCountry },
    });

    if (error) {
      setCardError(error);
      setIsSubmitting(false);
      return;
    }

    navigate(next);
    RemoveLocalStorage();
  };

  const handleCountryChange = (event) => {
    // The country sets the currency and prices for the whole site, cart included
    setCountry(event.target.value);
    setFormErrors(prev => ({ ...prev, phone: undefined }));

    setFormData(prev => ({
      ...prev,
      "country": event.target.value,
      "currency": currencyForCountry(event.target.value)
    }));

  };


  const validateForm = () => {
    const errors = {};

    // Check if all fields are filled
    Object.keys(formData).forEach((key) => {
      if (!formData[key]) {
        errors[key] = translate("This field is required");
      }
    });

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = translate("Invalid email address");
    }

    console.log(errors)

    // Phone must be a number from the shipping country
    const phoneProblem = phoneError(formData.phone, formData.country);
    if (phoneProblem) {
      errors.phone = translate(phoneProblem);
    }

    if (inlineCard) {
      if (!cardName.trim()) {
        errors.nameOnCard = translate("This field is required");
      }
      if (!sameBillingAddress) {
        Object.keys(billing).forEach((key) => {
          if (!billing[key].trim()) {
            errors[key] = translate("This field is required");
          }
        });
      }
    }



    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions; never place an order we can't ship
    if (isSubmitting || !shippingAvailable) {
      return;
    }

    if (validateForm()) {
      setIsSubmitting(true);
      setCardError(null);
      try {
        // A card that can't be charged shouldn't get as far as placing an order
        const inlinePayment = inlineCard ? cardFields.current : null;
        if (inlinePayment && !(await inlinePayment.validate())) {
          setIsSubmitting(false);
          return;
        }

        if (inlinePayment && placedOrderId) {
          await chargeInline(inlinePayment, placedOrderId);
          return;
        }

        const formDataToSend = new FormData();
        
        // Helper function to convert base64 to Blob with proper file extension
        const base64ToBlob = (base64String) => {
          const arr = base64String.split(',');
          const mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          return { blob: new Blob([u8arr], { type: mime }), mime: mime };
        };
        
        // Helper function to get file extension from mime type
        const getExtensionFromMime = (mimeType) => {
          const mimeMap = {
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/webp': 'webp'
          };
          return mimeMap[mimeType] || 'jpg';
        };
        
        // Add all form fields to FormData
        Object.keys(formData).forEach(key => {
          formDataToSend.append(key, formData[key]);
        });
        
        // Local format the couriers already get, e.g. "0641234567"
        formDataToSend.set('phone', localPhone(formData.phone, formData.country));

        // Add the converted shipping price that the user is actually paying
        formDataToSend.append('shippingCost', Math.round(shippingPrice));

        // Snapshot the delivery estimate the customer saw, so the confirmation email matches the UI exactly
        const leadDays = getCartLeadDays(AvailableCartItems, productsData, apiCountry);
        formDataToSend.append('deliveryStartDays', leadDays.startDays);
        formDataToSend.append('deliveryEndDays', leadDays.endDays);

        // Credit the order to the ad/campaign that first brought this visitor in
        const attribution = getOrderAttribution();
        Object.entries(attribution).forEach(([key, value]) => {
          if (value) formDataToSend.append(`attribution[${key}]`, value);
        });

        // Get client IP from frontend for Facebook Conversion API (more reliable than server-side)
        try {
          const ipRes = await fetch('https://api.ipify.org?format=json');
          if (ipRes.ok) {
            const { ip } = await ipRes.json();
            if (ip && typeof ip === 'string' && ip.length <= 45) formDataToSend.append('client_ip', ip);
          }
        } catch (_) {
          // Optional: order still submits without client_ip
        }

        // Add custom product images if any - convert base64 back to File objects
        if (OrderNowProduct) {
          // Handle OrderNowProduct images
          const orderNowCustomData = location.state?.customData || null;
          if (orderNowCustomData && orderNowCustomData.images && orderNowCustomData.images.length > 0) {
            orderNowCustomData.images.forEach((base64Image, imageIndex) => {
              const { blob, mime } = base64ToBlob(base64Image);
              const extension = getExtensionFromMime(mime);
              const filename = `product-${OrderNowProduct.id}-image-${imageIndex}.${extension}`;
              formDataToSend.append(`customImages[${OrderNowProduct.id}][${imageIndex}]`, blob, filename);
            });
          }
        } else {
          AvailableCartItems.forEach((cartItem) => {
            if (cartItem.customData && cartItem.customData.images && cartItem.customData.images.length > 0) {
              const imageKey = cartItem.cartKey || cartItem.id;
              cartItem.customData.images.forEach((base64Image, imageIndex) => {
                const { blob, mime } = base64ToBlob(base64Image);
                const extension = getExtensionFromMime(mime);
                const filename = `product-${cartItem.id}-image-${imageIndex}.${extension}`;
                formDataToSend.append(`customImages[${imageKey}][${imageIndex}]`, blob, filename);
              });
            }
          });
        }

        const response = await fetch(`${apiUrl}/api/placeorder`, {
          method: 'POST',
          body: formDataToSend,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        console.log('API response:', data);

        if (response.status === 201 && data.order.paymentMethod === CARD_PAYMENT) {
          // Not placed until paid — the payment page fires the purchase pixel and shows the thank-you
          if (inlinePayment) {
            setPlacedOrderId(data.order.orderId);
            await chargeInline(inlinePayment, data.order.orderId);
          } else {
            navigate(`/placanje/${encodeURIComponent(data.order.orderId)}`);
            RemoveLocalStorage();
          }
        } else if (response.status === 201) {
          const orderData = {
            ...data.order,
            shippingCost: shippingPrice,
            name: `${data.order.firstName} ${data.order.lastName}`
          };
          pixelPurchase(enrichedOrderProducts, totalAmount, currency, data.order.orderId);
          setApiData(orderData);
          setShowThankYou(true);
          RemoveLocalStorage();
        }
      } catch (error) {
        console.error(error.message);
        setIsSubmitting(false); // Re-enable button on error
      }
    } else {
      console.log("Form validation failed");
    }
  };

  console.log(ItemsInCart)

  React.useEffect(() => {
    console.log("Categories from props:", categories);
    console.log("OrderNowProduct:", OrderNowProduct);
    console.log("Category from state:", category);
    
    // Log the first category path for debugging
    if (categories && categories.length > 0) {
      console.log("First category path:", categories[0].path);
    }
  }, [categories, OrderNowProduct, category]);

  const availableCategories = locationCategories || categories;

  const isCardSelected = formData.paymentMethod === CARD_PAYMENT;
  const inlineCard = isCardSelected && Boolean(stripeKey) && formData.currency === 'EUR' && Number(totalAmount) > 0;
  const formattedShipping = !shippingAvailable ? "—"
    : Number(shippingPrice) === 0 ? translate("Free") : `${Math.round(shippingPrice)} ${currency}`;

  const hasPersonalMug = AvailableCartItems.some(item => {
    const p = productsData.find(pd => pd.id === item.id);
    return p && p.path?.toLowerCase().includes('personalna-ljubimac-soljica');
  });
  const hasPersonalPlate = AvailableCartItems.some(item => {
    const p = productsData.find(pd => pd.id === item.id);
    return p && p.path?.toLowerCase().includes('personalni-tanjir');
  });
  const deliveryDates = formatLeadDates(getCartLeadDays(AvailableCartItems, productsData, apiCountry));

  const paymentOptions = [
    ...(cashOnDeliveryAvailable ? [{
      value: CASH_ON_DELIVERY,
      label: translate("Cash on delivery"),
      description: translate("Pay in cash when the courier delivers your order."),
    }] : []),
    ...(cardPaymentAvailable ? [{
      value: CARD_PAYMENT,
      label: translate("Card payment"),
      description: inlineCard ? null : translate("You will enter your card details securely on the next step."),
      logos: [{ src: visaLogo, alt: "Visa" }, { src: mastercardLogo, alt: "Mastercard" }],
    }] : []),
  ];

  const orderSummary = (
    <div>
      <div className="divide-y divide-darkerSkin/70">
        {ItemsInCart}
      </div>

      <div className="mt-4 pt-4 border-t border-darkerSkin space-y-2 text-sm">
        <div className="flex justify-between text-gray-700">
          <span>{translate("Subtotal")}</span>
          <span className="font-medium text-gray-900">{Number(totalSubtotal).toFixed(2)} {currency}</span>
        </div>
        <div className="flex justify-between text-gray-700">
          <span>{translate("Delivery")}</span>
          <span className="font-medium text-gray-900">{formattedShipping}</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-darkerSkin flex items-baseline justify-between">
        <span className="text-lg font-semibold text-gray-900">{translate("Total")}</span>
        <span className="text-gray-900">
          <span className="text-xs text-gray-500 mr-1.5">{currency}</span>
          <span className="text-2xl font-semibold">{totalAmount}</span>
        </span>
      </div>

      {!OrderNowProduct && (
        <Link to="/cart" className="inline-block mt-4 text-sm text-brownSkin underline underline-offset-2 hover:text-gray-900">
          {translate("Edit Cart")}
        </Link>
      )}
    </div>
  );

  return (
    <div>
      <div className={showThankYou ? "hidden" : "font-sans"}>
        {/* Mobile: collapsible summary above the form */}
        <div className="1000:hidden border-b border-darkerSkin bg-bgSkin/60">
          <button
            type="button"
            onClick={() => setSummaryOpen(open => !open)}
            className="w-full flex items-center justify-between px-4 py-4 text-sm"
            aria-expanded={summaryOpen}
          >
            <span className="flex items-center gap-2 text-brownSkin font-medium">
              {summaryOpen ? translate("Hide order summary") : translate("Show order summary")}
              <svg className={`w-4 h-4 transition-transform ${summaryOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
            <span className="text-base font-semibold text-gray-900">{totalAmount} {currency}</span>
          </button>
          {summaryOpen && <div className="px-4 pb-5">{orderSummary}</div>}
        </div>

        <div className="1000:flex">
          {/* Form */}
          <div className="1000:w-[55%] 1000:flex 1000:justify-end">
            <form onSubmit={handleSubmit} noValidate className="w-full max-w-[580px] px-4 py-8 1000:px-12 1000:py-12">

              <fieldset disabled={Boolean(placedOrderId)}>
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">{translate("Contact")}</h2>
                <div className="space-y-3">
                  <Field label={translate("Confirmation email")} name="email" type="email" autoComplete="email" inputMode="email" value={formData.email} onChange={handleChange} error={formErrors.email} />
                  <PhoneField label={translate("Phone number")} country={formData.country} value={formData.phone} onChange={handleChange} error={formErrors.phone} />
                </div>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">{translate("Shipping information")}</h2>
                <div className="space-y-3">
                  <SelectField label={translate("Country")} name="country" value={formData.country} onChange={handleCountryChange} error={formErrors.country}>
                    {formData.country === "" && <option value="" disabled>{translate("Select country")}</option>}
                    {REGIONAL_COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{translate(c.label)}</option>
                    ))}
                    {/* The rest of the EU is card-only, so it's hidden while card payment is off */}
                    {CARD_PAYMENTS_ENABLED && (
                      <optgroup label={translate("European Union")}>
                        {sortedEuCountries(language, translate).map(c => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </SelectField>
                  <div className="grid grid-cols-1 500:grid-cols-2 gap-3">
                    <Field label={translate("First name")} name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleChange} error={formErrors.firstName} />
                    <Field label={translate("Last name")} name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleChange} error={formErrors.lastName} />
                  </div>
                  <Field label={translate("Address")} name="address" autoComplete="street-address" value={formData.address} onChange={handleChange} error={formErrors.address} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={translate("Zip code")} name="zip" autoComplete="postal-code" value={formData.zip} onChange={handleChange} error={formErrors.zip} />
                    <Field label={translate("City")} name="city" autoComplete="address-level2" value={formData.city} onChange={handleChange} error={formErrors.city} />
                  </div>
                </div>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">{translate("Shipping method")}</h2>
                {!shippingAvailable ? (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3.5 text-[15px] text-red-700">
                    {translate("Shipping to this country isn't available yet. Please contact us on Instagram.")}
                  </div>
                ) : (
                <div className="rounded-lg border border-brownSkin bg-bgSkin/40 px-4 py-3.5">
                  <div className="flex items-center justify-between gap-4 text-[15px]">
                    <span className="text-gray-900">{translate("Courier delivery to your address")}</span>
                    <span className="font-medium text-gray-900 whitespace-nowrap">{formattedShipping}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    {translate("Delivery within")} {deliveryDates.start} – {deliveryDates.end}
                  </p>
                  {hasPersonalMug && (
                    <p className="mt-1 text-xs text-gray-500 italic">
                      {translate("Due to handmade production and personalization, the mug takes about 4 weeks to make.")}
                    </p>
                  )}
                  {hasPersonalPlate && (
                    <p className="mt-1 text-xs text-gray-500 italic">
                      {translate("Due to handmade production and personalization, the plate takes about 4 weeks to make.")}
                    </p>
                  )}
                </div>
                )}
              </section>
              </fieldset>

              <section className="mt-8">
                <h2 className="text-xl font-semibold text-gray-900">{translate("Payment")}</h2>
                <p className="flex items-center gap-1.5 mt-1 mb-3 text-sm text-gray-500">
                  <svg className="w-3.5 h-3.5 flex-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  {translate("All transactions are secure and encrypted.")}
                </p>
                <div className="rounded-lg border border-gray-300 overflow-hidden divide-y divide-gray-300">
                  {paymentOptions.map(option => {
                    const selected = formData.paymentMethod === option.value;
                    return (
                      <div key={option.value}>
                        <label className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors ${selected ? 'bg-bgSkin/40' : 'bg-white hover:bg-gray-50'}`}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={option.value}
                            checked={selected}
                            onChange={handleChange}
                            disabled={Boolean(placedOrderId)}
                            className="w-4 h-4 accent-brownSkin"
                          />
                          <span className="flex-1 text-[15px] text-gray-900">{option.label}</span>
                          {option.logos && (
                            <span className="flex gap-1.5">
                              {option.logos.map(logo => (
                                <img key={logo.alt} src={logo.src} alt={logo.alt} className="h-6 w-auto rounded-[3px]" />
                              ))}
                            </span>
                          )}
                        </label>
                        {selected && option.description && (
                          <div className="bg-gray-50 px-4 py-3 pl-11 text-sm text-gray-600 border-t border-gray-200">
                            {option.description}
                          </div>
                        )}
                        {selected && option.value === CARD_PAYMENT && inlineCard && (
                          <div className="bg-gray-50 p-4 border-t border-gray-200">
                            <InlineCardPayment ref={cardFields} apiUrl={apiUrl} publishableKey={stripeKey} amount={totalAmount} language={language} />
                            <div className="mt-3 space-y-3">
                              <Field label={translate("Name on card")} name="nameOnCard" autoComplete="cc-name" value={cardName} onChange={(e) => setNameOnCard(e.target.value)} error={formErrors.nameOnCard} />
                              <label className="flex items-center gap-2.5 cursor-pointer text-[15px] text-gray-900">
                                <input
                                  type="checkbox"
                                  checked={sameBillingAddress}
                                  onChange={(e) => setSameBillingAddress(e.target.checked)}
                                  className="w-4 h-4 accent-brownSkin"
                                />
                                {translate("Use shipping address as billing address")}
                              </label>
                              {!sameBillingAddress && (
                                <>
                                  <SelectField label={translate("Country")} name="billingCountry" value={billing.billingCountry} onChange={handleBillingChange} error={formErrors.billingCountry}>
                                    <option value="" disabled>{translate("Select country")}</option>
                                    {REGIONAL_COUNTRIES.map(c => (
                                      <option key={c.code} value={c.code}>{translate(c.label)}</option>
                                    ))}
                                    <optgroup label={translate("European Union")}>
                                      {sortedEuCountries(language, translate).map(c => (
                                        <option key={c.code} value={c.code}>{c.name}</option>
                                      ))}
                                    </optgroup>
                                  </SelectField>
                                  <Field label={translate("Address")} name="billingAddress" autoComplete="billing street-address" value={billing.billingAddress} onChange={handleBillingChange} error={formErrors.billingAddress} />
                                  <div className="grid grid-cols-2 gap-3">
                                    <Field label={translate("Zip code")} name="billingZip" autoComplete="billing postal-code" value={billing.billingZip} onChange={handleBillingChange} error={formErrors.billingZip} />
                                    <Field label={translate("City")} name="billingCity" autoComplete="billing address-level2" value={billing.billingCity} onChange={handleBillingChange} error={formErrors.billingCity} />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {formErrors.paymentMethod && <p className="mt-1 text-xs text-red-600">{formErrors.paymentMethod}</p>}
                {cardError && (
                  <p role="alert" className="mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{translate(cardError)}</p>
                )}
              </section>

              <button
                type="submit"
                disabled={isSubmitting || !shippingAvailable}
                className={`w-full mt-8 rounded-lg px-6 py-4 text-base font-semibold transition-colors ${
                  isSubmitting || !shippingAvailable
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-brownSkin text-white hover:bg-[#8c5a30]'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {translate("Processing...")}
                  </span>
                ) : inlineCard ? `${translate("Pay")} ${totalAmount} ${currency}`
                  : isCardSelected ? translate("Continue to payment") : translate("Order")}
              </button>
            </form>
          </div>

          {/* Desktop: summary column */}
          <div className="hidden 1000:block 1000:w-[45%] bg-bgSkin/60 border-l border-darkerSkin">
            <div className="sticky top-0 max-w-[460px] px-12 py-12">
              {orderSummary}
            </div>
          </div>
        </div>
      </div>

      <div className={showThankYou ? "w-full items-center justify-center flex flex-col" : "hidden"}>
        <ThankYouForOrdering translate={translate} country={country} currency={currency} apiData={apiData} productsData={productsData} />
      </div>

      <Footer categories={availableCategories} />
    </div>
  )
}
